import { supabase, isSupabaseConfigured } from '@/lib/supabase';

// Reporte financiero agregado (P&L simplificado). No tiene tabla propia:
// se calcula en vivo desde sales, expenses, payments, commissions, donations,
// returns y campaigns.
export interface FinancialReport {
  grossSales: number;
  costTotal: number;
  grossProfit: number;
  grossMargin: number;
  expenses: number;
  advertising: number;
  commissions: number;
  donations: number;
  returns: number;
  netProfit: number;
  netMargin: number;
  salesCount: number;
  paidPayments: number;
  shippingCollected: number;
  shippingOperationalCost: number;
  // Inversión total consolidada de Marketing (inversión inicial de campañas +
  // registros del panel operativo). Se expone para la sección de reportes.
  marketingInvestment: number;
  marketingCampaigns: number;
  // Trazabilidad de pago contraentrega. Informativo: la comisión estimada de
  // Interrapidísimo frente al recargo cobrado al cliente. No se descuenta de la
  // utilidad neta para evitar doble conteo con ventas/fletes ya registrados.
  codCommissionEstimated: number;
  codSurchargeCollected: number;
  // Comisión real de recaudo contra entrega que se RESTA de la utilidad neta.
  // Misma regla que el Dashboard (Capital): solo pedidos ya facturados
  // (con sale_id) y no cancelados. Así ambas vistas cuadran igual.
  codFeesCost: number;
  codNetCost: number;
}

export interface ReportSummary {
  salesCount: number;
  orderCount: number;
  customerCount: number;
  productCount: number;
}

// Métricas separadas por línea comercial (premium / sencilla).
// - shippingCost: lo que la empresa paga por envíos de esa línea.
//   · Sencilla -> flete cobrado al cliente (orders.shipping_cost).
//   · Premium  -> flete incluido en el precio, sin cobro (orders.estimated_shipping_cost).
// - codDeliveries: número de contra entregas (pago contra entrega) con esa línea.
// - codCommission: comisión de Interrapidísimo por recaudar el pago contraentrega.
// - surchargeCollected: recargo contraentrega cobrado al cliente (solo sencilla).
export interface LineShipmentMetrics {
  shippingCost: number;
  codDeliveries: number;
  codCommission: number;
  surchargeCollected: number;
}

export interface LineShipmentReport {
  premium: LineShipmentMetrics;
  sencilla: LineShipmentMetrics;
}

// Códigos de método de pago que representan pago contra entrega.
const COD_PAYMENT_METHODS = new Set(['cash', 'cod', 'contra_entrega', 'contraentrega']);

// Reporte discriminado por línea comercial: cuánto se pagó por envíos y por
// contraentrega (comisión) en cada línea, teniendo en cuenta los pedidos mixtos
// (con productos premium y sencillos a la vez).
export async function fetchLineShipmentReport(): Promise<LineShipmentReport | null> {
  if (!isSupabaseConfigured || !supabase) return null;

  try {
    const [ordersRes, itemsRes, variantsRes, productsRes] = await Promise.all([
      supabase
        .from('orders')
        .select(
          'id, status, payment_method, shipping_cost, estimated_shipping_cost, cod_surcharge, sencilla_cod_commission, premium_cod_commission',
        ),
      supabase.from('order_items').select('order_id, variant_id'),
      supabase.from('product_variants').select('id, product_id'),
      supabase.from('products').select('id, line_type'),
    ]);

    if (ordersRes.error) {
      console.warn('[reports] line shipment orders query failed:', ordersRes.error.message);
      return null;
    }

    const safeRows = (res: { data: unknown; error: { message: string } | null }): Array<Record<string, any>> => {
      if (res.error) {
        console.warn('[reports] line shipment optional query failed:', res.error.message);
        return [];
      }
      return (res.data ?? []) as Array<Record<string, any>>;
    };

    const variantToLine = new Map<string, string>();
    const productLine = new Map<string, string>();
    safeRows(productsRes).forEach((p) => productLine.set(p.id, p.line_type ?? 'premium'));
    safeRows(variantsRes).forEach((v) =>
      variantToLine.set(v.id, productLine.get(v.product_id) ?? 'premium'),
    );

    // Marca cada pedido con las líneas que contiene.
    const hasSencilla = new Set<string>();
    const hasPremium = new Set<string>();
    safeRows(itemsRes).forEach((it) => {
      if (!it.order_id || !it.variant_id) return;
      const line = variantToLine.get(it.variant_id) ?? 'premium';
      if (line === 'sencilla') hasSencilla.add(it.order_id);
      else hasPremium.add(it.order_id);
    });

    const metrics: LineShipmentReport = {
      premium: { shippingCost: 0, codDeliveries: 0, codCommission: 0, surchargeCollected: 0 },
      sencilla: { shippingCost: 0, codDeliveries: 0, codCommission: 0, surchargeCollected: 0 },
    };

    safeRows(ordersRes)
      .filter((o) => o.status !== 'cancelled')
      .forEach((o) => {
        const isCod = COD_PAYMENT_METHODS.has(String(o.payment_method ?? '').toLowerCase());
        if (hasSencilla.has(o.id)) {
          metrics.sencilla.shippingCost += Number(o.shipping_cost ?? 0);
          if (isCod) {
            metrics.sencilla.codDeliveries += 1;
            metrics.sencilla.codCommission += Number(o.sencilla_cod_commission ?? 0);
            metrics.sencilla.surchargeCollected += Number(o.cod_surcharge ?? 0);
          }
        }
        if (hasPremium.has(o.id)) {
          metrics.premium.shippingCost += Number(o.estimated_shipping_cost ?? 0);
          if (isCod) {
            metrics.premium.codDeliveries += 1;
            metrics.premium.codCommission += Number(o.premium_cod_commission ?? 0);
          }
        }
      });

    return metrics;
  } catch (err) {
    console.warn('[reports] line shipment connection failed:', err);
    return null;
  }
}

export async function fetchFinancialReport(): Promise<FinancialReport | null> {
  if (!isSupabaseConfigured || !supabase) return null;

  try {
    const [salesRes, expensesRes, commissionsRes, paymentsRes, returnsRes, campaignsRes, donationsRes, ordersRes] =
      await Promise.all([
        supabase.from('sales').select('total, cost_total, gross_profit, voided_at, shipping_cost'),
        supabase.from('expenses').select('amount'),
        supabase.from('commissions').select('commission_amount, is_paid'),
        supabase.from('payments').select('amount, status'),
        supabase.from('returns').select('refund_amount, status'),
        // La inversión que consolida en los reportes es el costo declarado de
        // las campañas pausadas/finalizadas. El presupuesto tope NO se suma.
        supabase.from('campaigns').select('declared_cost, status'),
        supabase.from('donations').select('donation_generated'),
        supabase.from('orders').select('shipping_cost, sale_id, status, cod_surcharge, estimated_cod_commission'),
      ]);

    // Las ventas son la base del P&L: sin ellas no hay reporte. El resto de
    // tablas se tratan como opcionales, de modo que un error puntual en una sola
    // consulta no deje en blanco todo el Estado de Resultados.
    if (salesRes.error) {
      console.warn('[reports] sales query failed:', salesRes.error.message);
      return null;
    }
    const rows = (res: { data: unknown; error: { message: string } | null }): Array<Record<string, any>> => {
      if (res.error) {
        console.warn('[reports] optional query failed:', res.error.message);
        return [];
      }
      return (res.data ?? []) as Array<Record<string, any>>;
    };

    const sales = rows(salesRes).filter((r) => !r.voided_at);
    const grossSales = sales.reduce((s, r) => s + Number(r.total), 0);
    const costTotal = sales.reduce((s, r) => s + Number(r.cost_total), 0);
    const grossProfit = sales.reduce((s, r) => s + Number(r.gross_profit), 0);

    const expenses = rows(expensesRes).reduce((s, r) => s + Number(r.amount), 0);

    // Publicidad: SOLO el costo declarado de las campañas pausadas/finalizadas.
    // Los registros diarios del panel operativo NO afectan caja/reportes hasta
    // que la campaña se consolida (pausada o finalizada).
    const advertising = rows(campaignsRes)
      .filter((r) => r.status === 'paused' || r.status === 'finished')
      .reduce((s, r) => s + Number(r.declared_cost ?? 0), 0);
    const marketingCampaigns = rows(campaignsRes).length;

    const commissions = rows(commissionsRes)
      .filter((r) => r.is_paid)
      .reduce((s, r) => s + Number(r.commission_amount), 0);

    // Donación: se lee del roll-up real (tabla donations), no se recalcula.
    const donations = rows(donationsRes).reduce(
      (s, r) => s + Number(r.donation_generated ?? 0),
      0,
    );

    // Devoluciones efectivamente reembolsadas.
    const returns = rows(returnsRes)
      .filter((r) => r.status === 'refunded')
      .reduce((s, r) => s + Number(r.refund_amount ?? 0), 0);

    const paidPayments = rows(paymentsRes)
      .filter((r) => r.status === 'approved')
      .reduce((s, r) => s + Number(r.amount), 0);

    // Envíos cobrados al cliente: los ya facturados viven en sales.shipping_cost
    // y los pedidos aún no confirmados en orders.shipping_cost (para no doble contar).
    const salesShipping = sales.reduce((s, r) => s + Number(r.shipping_cost ?? 0), 0);
    const pendingOrderShipping = rows(ordersRes)
      .filter((o) => !o.sale_id)
      .reduce((s, o) => s + Number(o.shipping_cost ?? 0), 0);
    const shippingCollected = salesShipping + pendingOrderShipping;
    // El flete cobrado se paga a Interrapidísimo: costo operativo de envío.
    const shippingOperationalCost = shippingCollected;
    // Trazabilidad contraentrega: comisión estimada de Interrapidísimo vs el
    // recargo cobrado al cliente (solo babuchas sencillas).
    const codCommissionEstimated = rows(ordersRes).reduce(
      (s, o) => s + Number(o.estimated_cod_commission ?? 0),
      0,
    );
    const codSurchargeCollected = rows(ordersRes).reduce(
      (s, o) => s + Number(o.cod_surcharge ?? 0),
      0,
    );
    // Costo real de recaudo contra entrega: SOLO pedidos ya facturados (con
    // sale_id) y no cancelados. Es la MISMA base que usa el Dashboard en
    // `codFeesCost`, para que el Estado de Resultados y el Capital cuadren igual.
    const codFeesCost = rows(ordersRes)
      .filter((o) => o.sale_id && o.status !== 'cancelled')
      .reduce((s, o) => s + Number(o.estimated_cod_commission ?? 0), 0);
    const codNetCost = codCommissionEstimated - codSurchargeCollected;

    // La comisión de recaudo COD se resta igual que en el Dashboard: el recargo
    // cobrado al cliente ya viene dentro del `total` de la venta (ingreso), y su
    // costo asociado (lo que cobra la transportadora por recaudar) se descuenta
    // aquí para no inflar la utilidad neta.
    const netProfit =
      grossProfit +
      shippingCollected -
      shippingOperationalCost -
      returns -
      expenses -
      advertising -
      commissions -
      donations -
      codFeesCost;
    const grossMargin = grossSales > 0 ? (grossProfit / grossSales) * 100 : 0;
    const netMargin = grossSales > 0 ? (netProfit / grossSales) * 100 : 0;

    return {
      grossSales,
      costTotal,
      grossProfit,
      grossMargin,
      expenses,
      advertising,
      commissions,
      donations,
      returns,
      netProfit,
      netMargin,
      salesCount: sales.length,
      paidPayments,
      shippingCollected,
      shippingOperationalCost,
      marketingInvestment: advertising,
      marketingCampaigns,
      codCommissionEstimated,
      codSurchargeCollected,
      codFeesCost,
      codNetCost,
    };
  } catch (err) {
    console.warn('[reports] connection failed:', err);
    return null;
  }
}

export async function fetchReportSummary(): Promise<ReportSummary | null> {
  if (!isSupabaseConfigured || !supabase) return null;

  try {
    const [salesRes, ordersRes, customersRes, productsRes] = await Promise.all([
      supabase.from('sales').select('id', { count: 'exact', head: true }).is('voided_at', null),
      supabase.from('orders').select('id', { count: 'exact', head: true }),
      supabase.from('customers').select('id', { count: 'exact', head: true }),
      supabase.from('products').select('id', { count: 'exact', head: true }),
    ]);

    if (salesRes.error || ordersRes.error || customersRes.error || productsRes.error) {
      console.warn('[reports] summary query failed');
      return null;
    }

    return {
      salesCount: salesRes.count ?? 0,
      orderCount: ordersRes.count ?? 0,
      customerCount: customersRes.count ?? 0,
      productCount: productsRes.count ?? 0,
    };
  } catch (err) {
    console.warn('[reports] summary connection failed:', err);
    return null;
  }
}